import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const GalleryLightbox = ({ images, currentIndex, isOpen, onClose, onNext, onPrev }) => {
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrev();
          break;
        case 'ArrowRight':
          onNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrev]);

  // Get real image dimensions
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = images[currentIndex]?.url;
  }, [currentIndex, images]);

  // Calculate responsive dimensions based on image aspect ratio
  const getImageDimensions = () => {
    if (imageDimensions.width === 0 || imageDimensions.height === 0) {
      return { width: '90vw', height: '90vh' };
    }

    const imageAspectRatio = imageDimensions.width / imageDimensions.height;
    const windowAspectRatio = window.innerWidth / (window.innerHeight - 80);

    // If image is wider (landscape)
    if (imageAspectRatio > windowAspectRatio) {
      return { width: '90vw', height: 'auto' };
    }
    // If image is taller (portrait)
    else {
      return { width: 'auto', height: '90vh' };
    }
  };

  const imageDims = getImageDimensions();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300"
        >
          <SafeIcon icon={FiX} className="w-6 h-6" />
        </button>

        {/* Navigation Buttons */}
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300 hover:scale-110"
        >
          <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300 hover:scale-110"
        >
          <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
        </button>

        {/* Main Image Container - Adapts to real image dimensions */}
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="relative flex flex-col items-center justify-center"
          style={{
            width: imageDims.width,
            height: imageDims.height,
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Image Wrapper - Flex centering */}
          <div className="w-full h-full flex items-center justify-center rounded-lg overflow-hidden">
            <img
              src={images[currentIndex]?.url}
              alt={images[currentIndex]?.title}
              className="w-full h-full object-contain"
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </div>

          {/* Image Info - Positioned below image */}
          <div className="w-full bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 mt-4 rounded-lg">
            <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">{images[currentIndex]?.title}</h3>
            <div className="flex items-center justify-between text-white/80">
              <span className="text-sm">{currentIndex + 1} of {images.length}</span>
              <div className="flex space-x-1">
                {images.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex ? 'bg-white' : 'bg-white/40'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GalleryLightbox;